import { BaseEntityType } from '../../../types/entities';
import { entityService } from '../../entityService';
import { emitEvent, getActiveChatThread } from '../../worldEventsService';
import { API_BASE_URL } from '../../../api/config';

import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('INTEGRATIONSYSTEM_SERVICE');

export type IntegrationType = 'Gmail' | 'GoogleDrive' | 'StripeAtlas' | 'Dashlane' | 'AppleNote' | 'Notion' | 'GoogleCalendar' | 'GoogleAnalytics' | 'GoogleAds' | 'Figma' | 'LocalFiles' | 'Plaid' | 'GitHub' | 'Slack' | 'Dropbox' | 'GoogleSearchConsole' | 'AWS' | 'Spotify' | 'WhatsApp';

export interface IntegrationSetupField {
  key: string;
  label: string;
  masked: boolean;
  placeholder?: string;
}

export interface IntegrationDefinition {
  id: string;
  type: IntegrationType;
  name: string;
  icon: string;
  description: string;
  category: 'productivity' | 'finance' | 'communication' | 'storage' | 'security' | 'publishing';
  authType: 'oauth' | 'api_key' | 'manual' | 'file_upload';
  capabilities: string[];
  isAvailable: boolean;
  setupInstructions?: string;
  /** When true, multiple connected entities of this type are allowed (one per workspace/account). */
  allowMultiple?: boolean;
  /** For api_key authType: fields to collect during setup. */
  setupFields?: IntegrationSetupField[];
}

export interface IntegrationSkeleton {
  '@type': 'Integration';
  integrationType: IntegrationType;
  status: 'disconnected' | 'connecting' | 'connected' | 'error' | 'syncing' | 'pending';
  lastSyncAt?: string;
  connectionData?: {
    accountId?: string;
    accountName?: string;
    scopes?: string[];
    // Google Ads specific
    developerToken?: string;      // masked in UI — full value stored backend-side only in encrypted tokens blob
    developerTokenPending?: string;  // temporary during OAuth flow
    customerId?: string;          // selected default customer ID
    customers?: Array<{ customerId: string; accountName?: string }>;
  };
  syncStats?: {
    totalItems: number;
    lastSyncCount: number;
    errorCount: number;
  };
  metadata: Record<string, any>;
}

export interface IntegrationEntity extends BaseEntityType<IntegrationSkeleton> {
  entityType: 'Integration';
  skeleton: IntegrationSkeleton;
}

export interface IntegrationSyncResult {
  success: boolean;
  entitiesCreated: number;
  entitiesUpdated: number;
  errors: string[];
  metadata?: Record<string, any>;
}

// Core integration definitions available system-wide
export const INTEGRATION_DEFINITIONS: IntegrationDefinition[] = [
  {
    id: 'gmail',
    type: 'Gmail',
    name: 'Gmail',
    icon: 'simple-icons:gmail',
    description: 'Cut through email clutter with AI-powered search and summaries',
    category: 'communication',
    authType: 'oauth',
    capabilities: ['search', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Sign in with your Google account to authorize email access',
  },
  {
    id: 'whatsapp',
    type: 'WhatsApp',
    name: 'WhatsApp',
    icon: 'simple-icons:whatsapp',
    description: 'Connect your WhatsApp Business account for messaging',
    category: 'communication',
    authType: 'oauth',
    capabilities: ['messaging'],
    isAvailable: true,
    setupInstructions: 'Connect your WhatsApp Business account via Meta Business login',
  },
  {
    id: 'google_drive',
    type: 'GoogleDrive',
    name: 'Google Drive',
    icon: 'simple-icons:googledrive',
    description: 'Search your Drive files on-demand',
    category: 'storage',
    authType: 'oauth',
    capabilities: ['search', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Authorize access to search your Google Drive files',
  },
  {
    id: 'stripe_atlas',
    type: 'StripeAtlas',
    name: 'Stripe Atlas',
    icon: '💳',
    description: 'Keep company formation docs organized and accessible',
    category: 'finance',
    authType: 'manual',
    capabilities: ['document_import', 'company_setup'],
    isAvailable: false,
    setupInstructions: 'Open Stripe Atlas in browser and save important documents',
  },
  {
    id: 'dashlane',
    type: 'Dashlane',
    name: 'Dashlane',
    icon: '🔑',
    description: 'Automatically discover and organize your services',
    category: 'security',
    authType: 'file_upload',
    capabilities: ['password_import', 'service_discovery'],
    isAvailable: false,
    setupInstructions: 'Export your Dashlane data and upload the CSV file',
  },
  {
    id: 'apple_notes',
    type: 'AppleNote',
    name: 'Apple Notes',
    icon: '📝',
    description: 'Search and organize all your notes in one place',
    category: 'productivity',
    authType: 'manual',
    capabilities: ['note_import', 'attachment_import'],
    isAvailable: false,
  },
  {
    id: 'notion',
    type: 'Notion',
    name: 'Notion',
    icon: '📘',
    description: 'Bring your workspace knowledge into AI conversations',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['page_import', 'database_import'],
    isAvailable: false,
  },
  {
    id: 'figma',
    type: 'Figma',
    name: 'Figma',
    icon: 'figma',
    description: 'Reference and discuss designs with AI',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['file_import', 'design_sync'],
    isAvailable: false,
  },
  {
    id: 'google_calendar',
    type: 'GoogleCalendar',
    name: 'Google Calendar',
    icon: 'simple-icons:googlecalendar',
    description: 'Optimize your schedule and prep for meetings with AI',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['search', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Sign in with your Google account to authorize calendar access',
  },
  {
    id: 'plaid',
    type: 'Plaid',
    name: 'Plaid Banking',
    icon: '💳',
    description: 'Connect your bank accounts and track transactions automatically',
    category: 'finance',
    authType: 'oauth',
    capabilities: ['transaction_sync', 'balance_check', 'account_info'],
    isAvailable: true,
    setupInstructions: 'Sign in with your bank to authorize transaction access. Supports 10,000+ financial institutions.',
  },
  {
    id: 'google_analytics',
    type: 'GoogleAnalytics',
    name: 'Google Analytics',
    icon: 'simple-icons:googleanalytics',
    description: 'Query analytics data and generate insights with AI',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['search', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Sign in with your Google account to authorize analytics access',
  },
  {
    id: 'google_ads',
    type: 'GoogleAds',
    name: 'Google Ads',
    icon: 'simple-icons:googleads',
    description: 'Manage and analyze Google Ads campaigns with AI',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['query', 'mutate', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Enter your Google Ads developer token, then sign in with your Google account',
  },
  {
    id: 'local_files',
    type: 'LocalFiles',
    name: 'Local Files',
    icon: 'simple-icons:files',
    description: 'Work with any file on your computer using AI',
    category: 'storage',
    authType: 'manual',
    capabilities: ['file_import', 'folder_sync', 'real_time_sync'],
    isAvailable: false, // Hidden for now - will be re-enabled later
    setupInstructions: 'Connect to your local file system to access files',
  },
  {
    id: 'github',
    type: 'GitHub',
    name: 'GitHub',
    icon: 'simple-icons:github',
    description: 'Access your repositories and code',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['repo_access'],
    isAvailable: true,
    setupInstructions: 'Sign in with GitHub to authorize access to your repositories',
  },
  {
    id: 'slack',
    type: 'Slack',
    name: 'Slack',
    icon: 'simple-icons:slack',
    description: 'Sync DMs and contacts from your Slack workspaces',
    category: 'communication',
    authType: 'oauth',
    capabilities: ['send_dm', 'read_dm', 'contact_sync'],
    isAvailable: true,
    allowMultiple: true,
    setupInstructions: 'Connect each Slack workspace you want to sync DMs from',
  },
  {
    id: 'dropbox',
    type: 'Dropbox',
    name: 'Dropbox',
    icon: 'simple-icons:dropbox',
    description: 'Connect Dropbox to give the AI access to your files',
    category: 'storage',
    authType: 'oauth',
    capabilities: ['search', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Sign in with your Dropbox account to authorize file access',
  },
  {
    id: 'google_search_console',
    type: 'GoogleSearchConsole',
    name: 'Google Search Console',
    icon: 'simple-icons:googlesearchconsole',
    description: 'Query search analytics, index coverage, and performance data for your sites',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['search_analytics', 'index_status', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Sign in with your Google account to authorize Search Console access',
  },
  {
    id: 'aws',
    type: 'AWS',
    name: 'AWS',
    icon: 'aws',
    description: 'Access S3, EC2, Lambda, CloudWatch and other AWS services',
    category: 'storage',
    authType: 'api_key',
    capabilities: ['s3_access', 'ec2_management', 'lambda_invocation', 'cloudwatch_logs', 'on_demand_access'],
    isAvailable: true,
    setupInstructions: 'Enter your AWS Access Key ID and Secret Access Key',
    setupFields: [
      { key: 'accessKeyId', label: 'Access Key ID', masked: false, placeholder: 'AKIAIOSFODNN7EXAMPLE' },
      { key: 'secretAccessKey', label: 'Secret Access Key', masked: true, placeholder: 'Your secret access key' },
      { key: 'region', label: 'Default Region', masked: false, placeholder: 'us-east-1' },
    ],
  },
  {
    id: 'spotify',
    type: 'Spotify',
    name: 'Spotify',
    icon: 'simple-icons:spotify',
    description: 'Control Spotify playback and search music with AI',
    category: 'productivity',
    authType: 'oauth',
    capabilities: ['playback_control', 'search', 'on_demand_access'],
    isAvailable: false,
    setupInstructions: 'Sign in with your Spotify account to enable AI-powered music control. Requires Spotify Premium for playback control.',
  },
];

/**
 * Core Integration System for GalOS
 * Manages all external service integrations as a system primitive
 */
export class IntegrationSystem {
  private static instance: IntegrationSystem;
  private integrations: Map<string, IntegrationEntity> = new Map();
  private syncInProgress: Set<string> = new Set();

  static getInstance(): IntegrationSystem {
    if (!IntegrationSystem.instance) {
      IntegrationSystem.instance = new IntegrationSystem();
    }
    return IntegrationSystem.instance;
  }

  /**
   * Get all available integration definitions
   */
  getAvailableIntegrations(): IntegrationDefinition[] {
    return INTEGRATION_DEFINITIONS.filter(def => def.isAvailable);
  }

  /**
   * Get integration definition by type
   */
  getIntegrationDefinition(type: IntegrationType): IntegrationDefinition | undefined {
    return INTEGRATION_DEFINITIONS.find(def => def.type === type);
  }

  /**
   * Get all user's integration entities
   * Integrations are user-level, spaceId parameter is optional and not used
   */
  async getUserIntegrations(spaceId?: string): Promise<IntegrationEntity[]> {
    try {
      const entities = await entityService.getEntitiesByType({ entityType: 'Integration' });
      return entities.filter(entity => 
        (entity.entityType === 'Integration' || entity.entityType?.startsWith('Integration.')) && 
        entity.skeleton?.['@type'] === 'Integration'
      ) as IntegrationEntity[];
    } catch (error) {
      log.error('Failed to fetch user integrations:', error);
      return [];
    }
  }

  /**
   * Create a new integration entity
   * Integrations are user-level - spaceId is optional and only used if provided
   */
  async createIntegration(
    spaceId: string = '', 
    type: IntegrationType, 
    metadata: Record<string, any> = {}
  ): Promise<IntegrationEntity> {
    const definition = this.getIntegrationDefinition(type);
    if (!definition) {
      throw new Error(`Integration type ${type} not found`);
    }

    // Check for existing integrations of this type
    const existingIntegrations = await this.getUserIntegrations(spaceId);
    const existingOfType = existingIntegrations.filter(i => 
      i.skeleton.integrationType === type
    );

    // For multi-instance integrations (e.g. Slack workspaces), always create a new entity.
    // The backend /oauth-callback handles deduplication by workspaceId.
    if (!definition.allowMultiple) {
      // If there's already a connected one, return it
      const connectedIntegration = existingOfType.find(i => i.skeleton.status === 'connected');
      if (connectedIntegration) {
        log.log(`[IntegrationSystem] ${type} already connected, returning existing:`, connectedIntegration._id);
        return connectedIntegration;
      }
      
      // If there's a disconnected or pending one, backend will reactivate it (idempotent create)
      const disconnectedIntegration = existingOfType.find(i => 
        i.skeleton.status === 'disconnected' || i.skeleton.status === 'error' || i.skeleton.status === 'pending'
      );
      if (disconnectedIntegration) {
        log.log(`[IntegrationSystem] ${type} exists in ${disconnectedIntegration.skeleton.status} state, reactivating:`, disconnectedIntegration._id);
      }
    } else {
      log.log(`[IntegrationSystem] ${type} allows multiple connections — creating new entity for new workspace`);
    }

    // Use hierarchical entity type: Integration.GoogleDrive, Integration.Gmail, etc.
    const hierarchicalType = `Integration.${type}` as const;
    
    const integrationData: any = {
      name: definition.name,
      entityType: hierarchicalType,
      icon: definition.icon,
      skeleton: {
        '@type': 'Integration' as const,
        integrationType: type,
        status: 'disconnected' as const,
        syncStats: {
          totalItems: 0,
          lastSyncCount: 0,
          errorCount: 0,
        },
        metadata,
      },
    };
    
    // Only add spaceId if provided
    if (spaceId) {
      integrationData.spaceId = spaceId;
    }

    try {
      // Backend will either create new or reactivate existing (idempotent)
      const entity = await entityService.create(integrationData);
      const integrationEntity = entity as IntegrationEntity;
      
      // ✅ FIX: Ensure _id is a string, not an ObjectId object
      const entityId = typeof integrationEntity._id === 'string' 
        ? integrationEntity._id 
        : String(integrationEntity._id);
      
      this.integrations.set(entityId, integrationEntity);
      
      log.log(`[IntegrationSystem] Created/reactivated ${type} integration:`, entityId);
      
      // Return entity with stringified ID
      return {
        ...integrationEntity,
        _id: entityId
      } as IntegrationEntity;
    } catch (error) {
      log.error(`Failed to create ${type} integration:`, error);
      throw error;
    }
  }

  /**
   * Connect an integration (start auth flow)
   */
  async connectIntegration(integrationId: string): Promise<void> {
    // ✅ FIX: Ensure integrationId is always a string
    const id = typeof integrationId === 'string' ? integrationId : String(integrationId);
    
    const integration = await this.getIntegration(id);
    if (!integration) {
      throw new Error('Integration not found');
    }

    // Update status to connecting
    await this.updateIntegrationStatus(id, 'connecting');

    const definition = this.getIntegrationDefinition(integration.skeleton.integrationType);
    if (!definition) {
      throw new Error('Integration definition not found');
    }

    log.info(`[IntegrationSystem] connect ${integration.skeleton.integrationType} entity=${id}`);

    try {
      // Handle different auth types
      switch (definition.authType) {
        case 'oauth':
          await this.handleOAuthFlow(integration, definition);
          break;
        case 'api_key':
          await this.handleApiKeyFlow(integration, definition);
          break;
        case 'manual':
          await this.handleManualFlow(integration, definition);
          break;
        case 'file_upload':
          await this.handleFileUploadFlow(integration, definition);
          break;
        default:
          throw new Error(`Auth type ${definition.authType} not implemented`);
      }

      // Verify auth token is present before updating
      const authToken = localStorage.getItem('authToken');
      log.log(`[IntegrationSystem] Auth token check before update: ${!!authToken}`);
      
      if (!authToken) {
        log.error(`[IntegrationSystem] No auth token found after OAuth flow`);
        throw new Error('Authentication token not found. Please refresh the page and try again.');
      }

      // Update status to connected
      log.log(`[IntegrationSystem] About to call updateIntegrationStatus`);
      log.log(`[IntegrationSystem] Auth token still present: ${!!localStorage.getItem('authToken')}`);
      await this.updateIntegrationStatus(integrationId, 'connected');

      // Emit world event (fire-and-forget — never fatal)
      try {
        const spaceId = (integration as any).spaceId || '';
        const currentThreadId = getActiveChatThread(spaceId);
        emitEvent('integration.connected', {
          service: integration.skeleton.integrationType,
          serviceName: definition.name,
          integrationId,
          ...(currentThreadId ? { currentThreadId } : {}),
        }, spaceId);
      } catch { /* non-fatal */ }

      // Start initial sync (skip for tool-based integrations)
      const integrationType = integration.skeleton.integrationType;
      const noAutoSyncTypes = ['LocalFiles', 'GoogleDrive', 'Gmail', 'GoogleCalendar', 'GoogleAnalytics', 'GitHub', 'Slack'];
      
      if (!noAutoSyncTypes.includes(integrationType)) {
        await this.syncIntegration(id);
      } else {
        log.log(`[IntegrationSystem] Skipping auto-sync for ${integrationType} - tool-based or manual configuration`);
      }

    } catch (error) {
      log.error(`Failed to connect ${definition.name}:`, error);

      // Revert to error state — never delete the entity, which could be a pre-existing
      // reactivated entity with history the user would lose permanently.
      try {
        await this.updateIntegrationStatus(id, 'error');
      } catch (statusError) {
        log.error(`[IntegrationSystem] Failed to revert status to error:`, statusError);
      }
      
      // Provide user-friendly error messages
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('cancelled by user')) {
        throw new Error('Connection cancelled');
      } else if (errorMessage.includes('popup')) {
        throw new Error('Please allow popups for this site and try again');
      } else if (errorMessage.includes('timeout')) {
        throw new Error('Connection timed out. Please try again.');
      } else if (errorMessage.includes('redirect_uri')) {
        throw new Error('OAuth configuration error. Please contact support.');
      }
      
      throw error;
    }
  }

  /**
   * Sync data from an integration
   */
  async syncIntegration(integrationId: string): Promise<IntegrationSyncResult> {
    if (this.syncInProgress.has(integrationId)) {
      throw new Error('Sync already in progress');
    }

    const integration = await this.getIntegration(integrationId);
    if (!integration) {
      throw new Error('Integration not found');
    }

    // Allow syncing from 'connected', 'error', or 'syncing' (recovery) states
    const validStatuses = ['connected', 'error', 'syncing'];
    if (!validStatuses.includes(integration.skeleton.status)) {
      throw new Error(`Integration status is ${integration.skeleton.status}, must be connected first`);
    }

    this.syncInProgress.add(integrationId);
    await this.updateIntegrationStatus(integrationId, 'syncing');

    try {
      const result = await this.performSync(integration);
      
      // Update sync stats
      await this.updateSyncStats(integrationId, result);
      await this.updateIntegrationStatus(integrationId, 'connected');
      
      log.log(`[IntegrationSystem] Sync completed for ${integration.skeleton.integrationType}:`, result);
      return result;

    } catch (error) {
      log.error(`Sync failed for ${integration.skeleton.integrationType}:`, error);
      await this.updateIntegrationStatus(integrationId, 'error');
      
      // Re-throw with better error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown sync error';
      throw new Error(`Sync failed: ${errorMessage}`);
    } finally {
      this.syncInProgress.delete(integrationId);
    }
  }

  /**
   * Update integration connection data (tokens, account info)
   */
  async updateIntegrationConnectionData(integrationId: string, connectionData: {
    tokens: string;
    accountId: string;
    accountName: string;
    scopes: string[];
  }): Promise<void> {
    const integration = await this.getIntegration(integrationId);
    if (!integration) {
      throw new Error('Integration not found');
    }

    const updates = {
      skeleton: {
        ...integration.skeleton,
        connectionData,
      },
    };

    await entityService.update(integrationId, updates);
    log.log(`[IntegrationSystem] Updated connection data for integration:`, integrationId);
  }

  /**
   * Disconnect an integration
   */
  async disconnectIntegration(integrationId: string): Promise<void> {
    const integration = await this.getIntegration(integrationId);
    if (!integration) {
      throw new Error('Integration not found');
    }
    
    const type = integration.skeleton.integrationType;
    log.info(`[IntegrationSystem] disconnect ${type} entity=${integrationId}`);
    const definition = this.getIntegrationDefinition(type);
    
    // For OAuth and api_key integrations - call backend disconnect endpoint
    // Backend updates status to 'disconnected' and revokes the token
    if (['Gmail', 'GoogleCalendar', 'GoogleAnalytics', 'GoogleSearchConsole', 'GitHub', 'AWS'].includes(type)) {
      const serviceMap: Record<string, string> = {
        'Gmail': 'gmail',
        'GoogleCalendar': 'calendar',
        'GoogleAnalytics': 'google-analytics',
        'GoogleSearchConsole': 'google-search-console',
        'GitHub': 'github',
        'AWS': 'aws',
      };
      const servicePath = serviceMap[type];
      
      const { API_BASE_URL } = await import('../../../api/config');
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(
        `${API_BASE_URL}/integrations/${servicePath}/disconnect?integration_id=${integrationId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to disconnect ${type}: ${response.status}`);
      }
      
      // Backend updated status to 'disconnected', refresh local state
      await this.updateIntegrationStatus(integrationId, 'disconnected');
      log.log(`[IntegrationSystem] Disconnected ${type} (Composio-managed):`, integrationId);
      this.emitDisconnectedEvent(integration, definition);
      return;
    }
    
    // For GoogleDrive - use GoogleDriveAdapter.revokeAccess
    if (type === 'GoogleDrive') {
      const { googleDriveAdapter } = await import('../../integrations/adapters/GoogleDriveAdapter');
      await googleDriveAdapter.revokeAccess(integrationId);
      // Backend updated status, refresh local state
      await this.updateIntegrationStatus(integrationId, 'disconnected');
      log.log(`[IntegrationSystem] Disconnected GoogleDrive (status updated):`, integrationId);
      this.emitDisconnectedEvent(integration, definition);
      return;
    }
    
    // For other integrations - just update status
    await this.updateIntegrationStatus(integrationId, 'disconnected');
    log.log(`[IntegrationSystem] Disconnected integration (status updated):`, integrationId);
    this.emitDisconnectedEvent(integration, definition);
  }

  private emitDisconnectedEvent(integration: IntegrationEntity, definition: IntegrationDefinition | undefined): void {
    try {
      emitEvent('integration.disconnected', {
        service: integration.skeleton.integrationType,
        serviceName: definition?.name ?? integration.skeleton.integrationType,
        integrationId: integration._id,
      }, (integration as any).spaceId || '');
    } catch { /* non-fatal */ }
  }

  // Private helper methods

  private async getIntegration(integrationId: string): Promise<IntegrationEntity | null> {
    if (this.integrations.has(integrationId)) {
      return this.integrations.get(integrationId)!;
    }

    try {
      const entity = await entityService.get(integrationId);
      if (entity.entityType === 'Integration' || entity.entityType?.startsWith('Integration.')) {
        const integration = entity as IntegrationEntity;
        this.integrations.set(integrationId, integration);
        return integration;
      }
    } catch (error) {
      log.error('Failed to fetch integration:', error);
    }

    return null;
  }

  async updateIntegrationStatus(
    integrationId: string, 
    status: IntegrationEntity['skeleton']['status']
  ): Promise<void> {
    // Get current integration to preserve other skeleton fields (especially connectionData!)
    const integration = await entityService.get(integrationId);
    if (!integration) {
      throw new Error(`Integration ${integrationId} not found`);
    }
    
    // Merge status update with existing skeleton to preserve connectionData, syncStats, etc.
    const updates = {
      skeleton: {
        ...integration.skeleton,
        status,
        lastSyncAt: status === 'connected' ? new Date().toISOString() : undefined,
      },
    };

    await entityService.update(integrationId, updates);
    
    // Update local cache
    const cachedIntegration = this.integrations.get(integrationId);
    if (cachedIntegration) {
      cachedIntegration.skeleton.status = status;
      if (updates.skeleton.lastSyncAt) {
        cachedIntegration.skeleton.lastSyncAt = updates.skeleton.lastSyncAt;
      }
    }
  }

  private async updateSyncStats(
    integrationId: string, 
    result: IntegrationSyncResult
  ): Promise<void> {
    // Get current integration to preserve other skeleton fields
    const integration = await entityService.get(integrationId);
    if (!integration) {
      throw new Error(`Integration ${integrationId} not found`);
    }
    
    // Merge sync stats with existing skeleton
    const updates = {
      skeleton: {
        ...integration.skeleton,
        syncStats: {
          totalItems: result.entitiesCreated + result.entitiesUpdated,
          lastSyncCount: result.entitiesCreated + result.entitiesUpdated,
          errorCount: result.errors.length,
        },
        lastSyncAt: new Date().toISOString(),
      },
    };

    await entityService.update(integrationId, updates);
  }

  // Auth flow handlers
  private async handleOAuthFlow(integration: IntegrationEntity, definition: IntegrationDefinition): Promise<void> {
    log.log(`[IntegrationSystem] Starting OAuth flow for ${definition.name}`);
    
    // For Google Drive, use the GoogleDriveAdapter
    if (definition.type === 'GoogleDrive') {
      const { googleDriveAdapter } = await import('../../integrations/adapters/GoogleDriveAdapter');
      
      try {
        // Initiate OAuth flow (opens popup)
        const callbackData = await googleDriveAdapter.initiateOAuthFlow();
        
        log.log(`[IntegrationSystem] OAuth successful for ${definition.name}`, callbackData.accountId);
        log.log(`[IntegrationSystem] 🔍 Auth token present BEFORE connection data update: ${!!localStorage.getItem('authToken')}`);
        log.log(`[IntegrationSystem] 🔍 localStorage keys BEFORE update:`, Object.keys(localStorage));
        
        // Update integration entity with connection data
        try {
          log.log(`[IntegrationSystem] 🔍 About to update entity with connection data`);
          await entityService.update(integration._id, {
            skeleton: {
              ...integration.skeleton,
              connectionData: {
                tokens: callbackData.tokens,
                accountId: callbackData.accountId,
                accountName: callbackData.accountName,
                scopes: [
                  'openid',
                  'https://www.googleapis.com/auth/userinfo.profile',
                  'https://www.googleapis.com/auth/userinfo.email',
                  'https://www.googleapis.com/auth/drive.readonly',
                  'https://www.googleapis.com/auth/drive.metadata.readonly',
                ],
              },
            },
          });
          
          log.log(`[IntegrationSystem] ✅ Integration entity updated with OAuth tokens`);
          log.log(`[IntegrationSystem] 🔍 Auth token AFTER connection data update: ${!!localStorage.getItem('authToken')}`);
          log.log(`[IntegrationSystem] 🔍 localStorage keys AFTER update:`, Object.keys(localStorage));
        } catch (updateError) {
          log.error(`[IntegrationSystem] ❌ Failed to update integration with OAuth tokens:`, updateError);
          log.error(`[IntegrationSystem] 🔍 Auth token when update failed: ${!!localStorage.getItem('authToken')}`);
          throw updateError;
        }
        
        log.log(`[IntegrationSystem] 🔍 Exiting handleOAuthFlow - auth token: ${!!localStorage.getItem('authToken')}`);
      } catch (error) {
        log.error(`[IntegrationSystem] OAuth flow failed for ${definition.name}:`, error);
        throw error;
      }
    } 
    // Gmail uses the mail.py OAuth path (full scopes + ingest pipeline).
    // Calendar and Analytics use their own google_integrations routes.
    else if (['Gmail', 'GoogleCalendar', 'GoogleAnalytics', 'GoogleSearchConsole'].includes(definition.type)) {
      try {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popupFeatures = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

        const popup = window.open('about:blank', `${definition.name}Auth`, popupFeatures);
        if (!popup) {
          throw new Error('Failed to open popup window. Please allow popups for this site.');
        }

        // Get OAuth URL from backend (after blank popup — Safari blocks window.open after await).
        // Gmail goes through /api/mail/gmail/oauth-url so it gets the full scope
        // set and the ingest job is queued automatically on callback.
        // Calendar and Analytics use their own /api/integrations/<service>/connect routes.
        const authToken = localStorage.getItem('authToken');
        let response: Response;
        if (definition.type === 'Gmail') {
          response = await fetch(
            `${API_BASE_URL}/mail/gmail/oauth-url?integration_id=${integration._id}`,
            {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${authToken}` },
            }
          );
        } else {
          const serviceMap: Record<string, string> = {
            'GoogleCalendar': 'calendar',
            'GoogleAnalytics': 'google-analytics',
            'GoogleSearchConsole': 'google-search-console',
          };
          const servicePath = serviceMap[definition.type];
          response = await fetch(
            `${API_BASE_URL}/integrations/${servicePath}/connect`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ integrationId: integration._id }),
            }
          );
        }

        if (!response.ok) {
          popup.close();
          throw new Error(`Failed to get OAuth URL: ${response.status}`);
        }

        const data = await response.json();
        popup.location.href = data.authorization_url;
        
        // Wait for callback with proper error handling
        const callbackData = await new Promise<any>((resolve, reject) => {
          let checkClosed: NodeJS.Timeout | null = null;
          let timeout: NodeJS.Timeout | null = null;
          
          const cleanup = () => {
            if (checkClosed) {
              clearInterval(checkClosed);
              checkClosed = null;
            }
            if (timeout) {
              clearTimeout(timeout);
              timeout = null;
            }
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('message', handleError);
          };
          
          const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            
            const messageTypes: Record<string, string> = {
              'Gmail': 'oauth-gmail-success',
              'GoogleCalendar': 'oauth-google-calendar-success',
              'GoogleAnalytics': 'oauth-google-analytics-success',
              'GoogleSearchConsole': 'oauth-google-search-console-success',
            };
            
            const expectedMessageType = messageTypes[definition.type];
            if (expectedMessageType && event.data.type === expectedMessageType) {
              log.log(`[IntegrationSystem] ✅ OAuth success for ${definition.name}`);
              cleanup();
              try {
                if (popup && !popup.closed) popup.close();
              } catch (e) {}
              resolve(event.data.data || {});
            }
          };
          
          const handleError = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            
            const errorTypes: Record<string, string> = {
              'Gmail': 'oauth-gmail-error',
              'GoogleCalendar': 'oauth-google-calendar-error',
              'GoogleAnalytics': 'oauth-google-analytics-error',
              'GoogleSearchConsole': 'oauth-google-search-console-error',
            };
            
            const expectedErrorType = errorTypes[definition.type];
            if (expectedErrorType && event.data.type === expectedErrorType) {
              log.error(`[IntegrationSystem] ❌ OAuth error for ${definition.name}:`, event.data.error);
              cleanup();
              try {
                if (popup && !popup.closed) popup.close();
              } catch (e) {}
              reject(new Error(event.data.error || 'OAuth failed'));
            }
          };
          
          window.addEventListener('message', handleMessage);
          window.addEventListener('message', handleError);
          
          // Check if popup is closed (user cancelled)
          checkClosed = setInterval(() => {
            try {
              if (popup && popup.closed) {
                log.warn(`[IntegrationSystem] User cancelled OAuth for ${definition.name}`);
                cleanup();
                reject(new Error('OAuth cancelled by user'));
              }
            } catch (e) {
              // COOP prevents checking popup.closed - rely on timeout
            }
          }, 1000);
          
          // Timeout after 5 minutes
          timeout = setTimeout(() => {
            log.error(`[IntegrationSystem] OAuth timeout for ${definition.name}`);
            cleanup();
            try {
              if (popup && !popup.closed) popup.close();
            } catch (e) {}
            reject(new Error('OAuth timeout - please try again'));
          }, 5 * 60 * 1000);
        });
        
        log.log(`[IntegrationSystem] OAuth successful for ${definition.name}`, callbackData);
        
        // Integration entity already updated by backend callback
        // Just log success
        log.log(`[IntegrationSystem] ✅ ${definition.name} connected`);
        
      } catch (error) {
        log.error(`[IntegrationSystem] Composio OAuth flow failed for ${definition.name}:`, error);
        throw error;
      }

      // Legacy popup code kept here as dead block — remove after Composio validation
    } 
    else if (definition.type === 'GitHub') {
      try {
        const { connectComposioToolkit } = await import('../../../services/composioService');
        await connectComposioToolkit('GitHub');
        log.log('[IntegrationSystem] ✅ GitHub connected via Composio');
      } catch (error) {
        log.error('[IntegrationSystem] GitHub Composio OAuth failed:', error);
        throw error;
      }
    }
    else if (definition.type === 'Slack') {
      const { API_BASE_URL } = await import('../../../api/config');

      try {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popupFeatures = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

        const popup = window.open('about:blank', 'SlackAuth', popupFeatures);
        if (!popup) {
          throw new Error('Failed to open popup window. Please allow popups for this site.');
        }

        const authToken = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/integrations/slack/connect`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ integrationId: integration._id }),
        });

        if (!response.ok) {
          popup.close();
          let detail = `Failed to get Slack OAuth URL: ${response.status}`;
          try {
            const body = await response.json();
            if (body.detail) detail = body.detail;
          } catch {}
          throw new Error(detail);
        }

        const data = await response.json();
        popup.location.href = data.authorization_url;

        await new Promise<void>((resolve, reject) => {
          let checkClosed: NodeJS.Timeout | null = null;
          let timeout: NodeJS.Timeout | null = null;

          const cleanup = () => {
            if (checkClosed) { clearInterval(checkClosed); checkClosed = null; }
            if (timeout) { clearTimeout(timeout); timeout = null; }
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('message', handleError);
          };

          const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === 'oauth-slack-success') {
              log.log('[IntegrationSystem] Slack OAuth success');
              cleanup();
              try { if (popup && !popup.closed) popup.close(); } catch (e) {}
              resolve();
            }
          };

          const handleError = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === 'oauth-slack-error') {
              log.error('[IntegrationSystem] Slack OAuth error:', event.data.error);
              cleanup();
              try { if (popup && !popup.closed) popup.close(); } catch (e) {}
              reject(new Error(event.data.error || 'Slack OAuth failed'));
            }
          };

          window.addEventListener('message', handleMessage);
          window.addEventListener('message', handleError);

          checkClosed = setInterval(() => {
            try {
              if (popup && popup.closed) {
                log.warn('[IntegrationSystem] User cancelled Slack OAuth');
                cleanup();
                reject(new Error('OAuth cancelled by user'));
              }
            } catch (e) {}
          }, 1000);

          timeout = setTimeout(() => {
            cleanup();
            try { if (popup && !popup.closed) popup.close(); } catch (e) {}
            reject(new Error('OAuth timeout - please try again'));
          }, 5 * 60 * 1000);
        });

        log.log('[IntegrationSystem] ✅ Slack workspace connected');
      } catch (error) {
        log.error('[IntegrationSystem] Slack OAuth failed:', error);
        throw error;
      }
    }
    else {
      // For other OAuth integrations - not yet implemented
      log.log(`[IntegrationSystem] OAuth flow for ${definition.name} - Not yet implemented`);
      throw new Error(`OAuth flow not implemented for ${definition.type}`);
    }
  }

  private async handleApiKeyFlow(integration: IntegrationEntity, definition: IntegrationDefinition): Promise<void> {
    // TODO: Implement API key flow
    log.log(`[IntegrationSystem] API key flow for ${definition.name} - TODO: Implement`);
  }

  private async handleManualFlow(integration: IntegrationEntity, definition: IntegrationDefinition): Promise<void> {
    // For manual integrations like Stripe Atlas
    log.log(`[IntegrationSystem] Manual flow for ${definition.name} - User guided setup`);
  }

  private async handleFileUploadFlow(integration: IntegrationEntity, definition: IntegrationDefinition): Promise<void> {
    // For file-based integrations like Dashlane export
    log.log(`[IntegrationSystem] File upload flow for ${definition.name} - TODO: Implement`);
  }

  // Sync implementation
  private async performSync(integration: IntegrationEntity): Promise<IntegrationSyncResult> {
    const type = integration.skeleton.integrationType;
    
    // Tool-based integrations - no sync/ingestion, only on-demand AI access
    if (['GoogleDrive', 'Gmail', 'GoogleCalendar', 'GoogleAnalytics', 'GoogleAds', 'Slack'].includes(type)) {
      const messages: Record<string, string> = {
        'GoogleDrive': 'Google Drive is search-only. Use search to find files on-demand.',
        'Gmail': 'Gmail is tool-based. Use the email_read AI tool to search emails on-demand.',
        'GoogleCalendar': 'Calendar is tool-based. Use the calendar_read AI tool to query events on-demand.',
        'GoogleAnalytics': 'Analytics is tool-based. Use the google_analytics_query AI tool to query data on-demand.',
        'GoogleAds': 'Google Ads is tool-based. Use the google_ads_query and google_ads_mutate AI tools to manage campaigns on-demand.'
      };
      
      log.info(`[IntegrationSystem] ${type} is tool-based - no sync/ingestion performed`);
      
      return {
        success: true,
        entitiesCreated: 0,
        entitiesUpdated: 0,
        errors: [messages[type] || 'Tool-based integration. Use AI tools for on-demand access.'],
        metadata: {
          syncedAt: new Date().toISOString(),
          integrationType: type,
          mode: 'tool-based',
          note: 'AI tools provide real-time access without entity creation',
        },
      };
    }
    
    // Local Files sync
    if (type === 'LocalFiles') {
      const { localFilesAdapter } = await import('../../integrations/adapters/LocalFilesAdapter');
      
      try {
        // Check if paths have been configured
        const paths = integration.skeleton.metadata?.paths || [];
        if (paths.length === 0) {
          log.log('[IntegrationSystem] No paths configured for LocalFiles sync - skipping');
          return {
            success: true,
            entitiesCreated: 0,
            entitiesUpdated: 0,
            errors: ['No folders configured. Please configure sync to select folders.'],
            metadata: {
              syncedAt: new Date().toISOString(),
              integrationType: type,
            },
          };
        }
        
        const tree = await localFilesAdapter.syncFiles(integration._id, paths);
        
        // Create LocalFile entities for each file/folder in the tree
        const createdEntities: string[] = [];
        const errors: string[] = [];
        
        // PERFORMANCE FIX: Fetch all entities once at the start, not for each file
        log.log('[IntegrationSystem] Fetching existing entities for duplicate check...');
        const allEntities = await entityService.getAllUserEntities();
        const existingLocalFiles = allEntities.filter((e: any) => 
          (e.entityType === 'LocalFile' || e.entityType?.startsWith('External.LocalFile')) &&
          e.skeleton?.integrationId === integration._id
        );
        
        // Build a map for O(1) lookups
        const existingFileMap = new Map(
          existingLocalFiles.map((e: any) => [e.skeleton?.path, e])
        );
        
        log.log(`[IntegrationSystem] Found ${existingLocalFiles.length} existing LocalFiles, syncing ${Object.keys(tree).length} files...`);
        
        for (const [filePath, fileData] of Object.entries(tree)) {
          try {
            // Check if entity already exists using the map
            const existingEntity = existingFileMap.get(filePath);
            
            // Determine hierarchical entity type
            const entityType = fileData.isFolder ? 'External.LocalFile.Directory' : 'External.LocalFile';
            
            if (existingEntity) {
              // Update existing entity and migrate to hierarchical type if needed
              const updates: any = {
                ...existingEntity,
                name: fileData.name,
                skeleton: {
                  ...existingEntity.skeleton,
                  '@type': 'LocalFile',
                  localFileId: filePath,
                  integrationId: integration._id,
                  name: fileData.name,
                  path: filePath,
                  isDirectory: fileData.isFolder,
                  isFile: fileData.isFile,
                  size: fileData.size,
                  modified: fileData.modified,
                },
              };
              
              // Migrate legacy entityType to hierarchical type
              if (existingEntity.entityType === 'LocalFile') {
                updates.entityType = entityType;
                log.log(`[IntegrationSystem] Migrating LocalFile to ${entityType} for: ${fileData.name}`);
              }
              
              await entityService.update(existingEntity._id, updates);
            } else {
              // Create new entity with hierarchical type
              const entity = await entityService.create({
                name: fileData.name,
                entityType: entityType,
                icon: fileData.isFolder ? 'folder' : 'file',
                skeleton: {
                  '@type': 'LocalFile',
                  localFileId: filePath,
                  integrationId: integration._id,
                  name: fileData.name,
                  path: filePath,
                  isDirectory: fileData.isFolder,
                  isFile: fileData.isFile,
                  size: fileData.size,
                  modified: fileData.modified,
                },
              });
              createdEntities.push(entity._id);
            }
          } catch (error) {
            log.error(`[IntegrationSystem] Error creating LocalFile entity for ${filePath}:`, error);
            errors.push(`Failed to create entity for ${filePath}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
        
        log.log(`[IntegrationSystem] Synced ${Object.keys(tree).length} local files (${createdEntities.length} created)`);
        
        return {
          success: errors.length === 0,
          entitiesCreated: createdEntities.length,
          entitiesUpdated: Object.keys(tree).length - createdEntities.length,
          errors,
          metadata: {
            syncedAt: new Date().toISOString(),
            integrationType: type,
            fileCount: Object.keys(tree).length,
          },
        };
      } catch (error) {
        log.error(`[IntegrationSystem] Local Files sync failed:`, error);
        return {
          success: false,
          entitiesCreated: 0,
          entitiesUpdated: 0,
          errors: [error instanceof Error ? error.message : 'Unknown error'],
          metadata: {
            syncedAt: new Date().toISOString(),
            integrationType: type,
          },
        };
      }
    }
    
    // Mock sync results for other integrations - TODO: Implement actual sync logic
    const mockResult: IntegrationSyncResult = {
      success: true,
      entitiesCreated: Math.floor(Math.random() * 10) + 1,
      entitiesUpdated: Math.floor(Math.random() * 5),
      errors: [],
      metadata: {
        syncedAt: new Date().toISOString(),
        integrationType: type,
      },
    };

    log.log(`[IntegrationSystem] Mock sync for ${type}:`, mockResult);
    return mockResult;
  }
}

// Export singleton instance
export const integrationSystem = IntegrationSystem.getInstance(); 